<template>
  <li
    class="UserItem flex items-center border-t"
    :class="{'is-locked': user.downgrade}"
  >
    <user-info
      :id="user._id"
      :label="user.username"
      class="w-2/3 md:w-3/5 mr-2"
    >
      <template v-if="hasAdminRights">
        {{ user.email }}
      </template>
    </user-info>
    <user-role
      :user-role="userRole"
      :manager-scope="managerScope"
      :actors="user.managedActors"
    />
    <lm-icon-button
      v-if="user.downgrade"
      icon="lock"
      :disabled="!isOwner"
      class="ml-auto"
      svg
      @click="showUpgrade"
    />
    <template v-else>
      <lm-dropdown
        v-if="showOptions"
        class="flex-shrink-0 ml-auto"
        :items="options"
        auto-close
        @select="onSelect"
      />
      <div v-else-if="hasAdminRights" class="w-6 h-6 flex-shrink-0 ml-auto" />
    </template>
  </li>
</template>

<script>
import UserRole from '@/components/ui/usersTable/UserRole'
import {mapGetters} from 'vuex'
import UserInfo from '@/components/landscape/topics/UserInfo'
import LmDropdown from '@/components/ui/LmDropdown'
import {Events} from '@/lib/definitions'
import EventHub from '@/lib/eventHub'
import LmIconButton from '@/components/ui/LmIconButton'
import {Modal, Roles} from '@/lib/definitions'

export default {
  name: 'UserItem',
  components: {
    UserRole,
    LmIconButton,
    LmDropdown,
    UserInfo
  },
  props: {
    user: {
      type: Object,
      required: true
    }
  },
  computed: {
    ...mapGetters(['currentLandscape', 'hasAdminRights', 'userId', 'isOwner']),
    showOptions() {
      return this.hasAdminRights && !this.userIsOwner && !this.currentUser
    },
    currentUser() {
      return this.user._id === this.userId
    },
    userIsOwner() {
      return this.user.role === Roles.OWNER
    },
    userRole() {
      let role = this.user.role
      if (this.user.role === 'OWNER') {
        role = 'Account Owner'
      }
      return role.toLowerCase()
    },
    managerScope() {
      if (this.user.role === Roles.MANAGER) {
        return this.user.managedActors.length === 0 ? 'Global' : 'Scoped'
      }
      return false
    },
    options() {
      let t = []
      if (this.user.role === Roles.MANAGER) {
        t.push({action: 'managerScope', label: 'Change Manager scope'})
      }
      if (this.user.role !== Roles.MEMBER) {
        t.push({action: 'member', label: 'Set as Member'})
      }
      if (this.user.role !== Roles.MANAGER) {
        t.push({action: 'manager', label: 'Set as Manager'})
      }
      if (this.user.role !== Roles.ADMIN) {
        t.push({action: 'admin', label: 'Set as Admin'})
      }
      if (this.hasAdminRights) {
        t.push({action: 'delete', label: 'Delete'})
      }
      return t
    }
  },
  methods: {
    onSelect(item) {
      const r = item.action.toUpperCase()
      if (r === Roles.MANAGER || item.action === 'managerScope') {
        this.openManagerModal()
      } else if (r === Roles.ADMIN || r === Roles.MEMBER) {
        this.setRole(item.action)
      } else if (item.action === 'delete') {
        this.deleteUser()
      }
    },
    openManagerModal() {
      this.$router.pushQuery({
        submodal: Modal.MANAGER,
        user: this.user._id
      })
    },
    setRole(role) {
      this.$store
        .dispatch('setRole', {role, id: this.user._id})
        .then(() => {
          let msg = `${this.user.username} is now `
          msg += this.$options.filters.roleArticle(role)
          EventHub.emit(Events.SHOW_SNACKBAR, msg)
        })
        .catch(error => this.$emit('error', error))
    },
    deleteUser() {
      const params = {
        entity: 'user',
        prompt: `Are you sure you want to remove ${this.user.email} from ${this.currentLandscape.name}?`
      }
      EventHub.emit(Events.OPEN_CONFIRM, params, this.doDeleteUser)
    },
    doDeleteUser() {
      this.$store
        .dispatch('removeUser', {userId: this.user._id})
        .then(() => {
          let msg = `${this.user.username} has been removed`
          EventHub.emit(Events.SHOW_SNACKBAR, msg)
          this.$emit('delete')
        })
        .catch(error => this.$emit('error', error))
    },
    showUpgrade() {
      this.$router.pushQuery({submodal: Modal.UPGRADE_PLAN})
    }
  }
}
</script>

<style scoped>
.UserItem {
  height: 80px;
  .UserLock {
    padding-right: 4px;
    color: #969696;
    &:not(.is-disabled):hover {
      color: theme('colors.highlight');
    }
  }
  /* Locked */
  &.is-locked {
    .UserInfo,
    .Role {
      opacity: 0.5;
    }
  }
}
</style>
<style>
.UserItem.is-locked .UserLock {
  width: 24px;
  i {
    font-size: 18px;
  }
}
</style>
